import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Loader2, UserPlus, ShieldCheck, AlertCircle } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  admin: "Administrador",
  operator: "Operador",
  quality: "Qualidade",
  shift_leader: "Chefe de Turno",
  display: "Display (Ecrã)",
};

const SHIFT_LABELS = {
  morning: "Manhã",
  afternoon: "Tarde",
  night: "Noite",
};

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [userCode, setUserCode] = useState("");
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await axios.get(`${API}/invitations/validate/${token}`);
        setInvitation(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Convite inválido");
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userCode || !name || !pin) { toast.error("Preencha todos os campos"); return; }
    if (pin.length < 4 || pin.length > 10) { toast.error("PIN entre 4 e 10 caracteres"); return; }
    if (pin !== pin2) { toast.error("PINs não coincidem"); return; }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/invitations/complete`, {
        token,
        user_code: userCode.toUpperCase(),
        name,
        pin,
      });
      toast.success("Conta criada com sucesso! Faça login.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      showError(err, "Erro ao criar conta");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          <p className="text-slate-500 mt-3 text-sm">A validar convite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-6">
        <Card className="max-w-md w-full" data-testid="invite-error">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Convite inválido</h2>
            <p className="text-slate-600 text-sm">{error}</p>
            <Button onClick={() => navigate("/login")} className="mt-4" data-testid="invite-error-back">
              Ir para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-6" data-testid="invite-page">
      <Card className="max-w-md w-full shadow-xl">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-xl">
            <UserPlus className="h-6 w-6" />
            Criar Conta - Convite EP3D
          </CardTitle>
          <p className="text-sm text-blue-100 mt-1">
            Convite válido para perfil <strong>{ROLE_LABELS[invitation.role] || invitation.role}</strong>
            {invitation.shift && <> · Turno <strong>{SHIFT_LABELS[invitation.shift] || invitation.shift}</strong></>}
          </p>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Código de Utilizador *</Label>
              <Input
                value={userCode}
                onChange={(e) => setUserCode(e.target.value.toUpperCase())}
                placeholder="Ex: OP011"
                className="h-12 font-mono text-lg"
                maxLength={10}
                data-testid="invite-user-code"
              />
              <p className="text-[11px] text-slate-500 mt-1">Letras maiúsculas e números, ex: OP011</p>
            </div>
            <div>
              <Label>Nome Completo *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome e apelido"
                className="h-12"
                data-testid="invite-name"
              />
            </div>
            <div>
              <Label>PIN (4 a 10 dígitos) *</Label>
              <Input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="••••••"
                className="h-12 font-mono tracking-widest text-center text-2xl"
                maxLength={10}
                data-testid="invite-pin"
              />
            </div>
            <div>
              <Label>Confirmar PIN *</Label>
              <Input
                type="password"
                value={pin2}
                onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="••••••"
                className="h-12 font-mono tracking-widest text-center text-2xl"
                maxLength={10}
                data-testid="invite-pin2"
              />
              {pin && pin2 && pin !== pin2 && (
                <p className="text-xs text-red-500 mt-1">PINs não coincidem</p>
              )}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                O seu PIN será encriptado e nem o administrador o poderá ler.
                Se esquecer, peça reset ao administrador.
              </div>
            </div>
            <Button type="submit" className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700" disabled={submitting} data-testid="invite-submit">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Criar Conta"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
